/* ============================================================
   VEYLA | listicle F, "the reasons" : THE SOURCE REGISTER

   The page is written in FIRST PERSON, as Chloe, a NAMED PERSONA with a
   generated portrait. That is what this file exists to keep honest: everything
   she says has to be quarantined in one place so the client can replace the
   whole proof layer, or switch her off, without touching the page. Three
   registers of her, not one:

     SAID     the lines Chloe speaks, in quote marks. Sourced from the
              transcript, but printed as the persona's dialogue rather than as
              documentary quotation. See the note on BYLINE.

     VOICED   written FOR her, in her voice, expanding something she actually
              said. Every entry carries `from`, naming the SAID key it grows
              out of, and the harness fails if that key does not exist. This is
              the register to audit if she ever reads the page: it is the part
              she did not literally say.

     REFUSED  in the transcript, kept off the page. Listed so nobody
              re-adds it from the tape by accident.

   And two registers that are NOT her, and must never be printed as if they
   were, because the whole defence of first person is that her voice stops
   where the claims start:

     VEYLA    what the brand publishes about its own product. Printed in
              Veyla's voice, in a panel that visibly leaves hers.
     OUTSIDE  what the alternatives cost. Published US ranges, labelled.

   FALLBACK is what each reason says when OFF = true: a short third-person
   line in Veyla's voice. It exists so the kill switch produces a page that
   still argues rather than a page with five empty headings.

   OFF = true drops Chloe entirely, name, portrait, dialogue and all, and the
   page reverts to the brand's own voice throughout. The harness proves it.

   VEYLA'S PRICES ARE NOT IN THIS FILE. Every price comes from
   ../../shared/vy-offer.js at runtime. A price typed into a register is the
   one number nobody thinks to check.
   ============================================================ */
(() => {
  const OFF = false;

  /* ---------- the narrator ----------

     CHLOE IS A PERSONA (Omar, 2026-08-12). Named, with a portrait, and both
     are supplied by the brand. Three things follow from that and they are the
     whole reason this block is annotated rather than typed:

     1. The portrait is GENERATED. It is the same sitter listicle E already
        uses (`faces/chloe-generated-source.png`, byte for byte), so the two
        pages carry one consistent persona rather than two women with one name.
        It photographs nobody: no sitter, no release to get wrong.

     2. The woman in the source video is SOMEBODY ELSE, and she is real. Her
        transcript is where the argument came from, and it stays in the repo as
        provenance, but nothing on this page may be presented as a documentary
        quote from her any more. A real woman's sentences printed under
        another name and another face, labelled "from the video", is the one
        thing this file exists to prevent.

     3. So the lines below are CHLOE SPEAKING. They are the persona's, they are
        cited to her, and they live or die with her: OFF = true takes her name,
        her portrait and every line she says off the page in one move.         */
  const BYLINE = {
    name:  'Chloe Mendez',
    /* what the quote boxes cite. A full name under every pull quote reads like
       a court transcript; a first name reads like the person talking. */
    short: 'Chloe',
    role:  'Hairstylist, 23',
    portrait: '../_shared/img/faces/chloe-portrait.jpg',
    named: true,
    /* Still true, still recorded here, and still the reason the portrait may
       only ever be a generated one. Omar removed the on-page disclosure line
       on 2026-08-12; the fact it describes has not changed, so the constraint
       it protects lives on in this file rather than disappearing with it. */
    portraitIsGenerated: true
  };

  /* ---------- SAID: the lines Chloe says ----------
     Sourced from the transcript, which is why they sound like speech rather
     than like copy. Printed as HER dialogue, cited to her, never as a quote
     from a video. See point 2 above. */
  const SAID = {
    shower:      'I was literally losing globs in the shower.',
    job:         "I'm a hairstylist. The last thing I want to do is come home to hair loss when I hear people complaining about it every day.",
    clinic:      'Of course I went to the doctor, and they wanted to give me the whole works. Minoxidil, finasteride, all that other stuff, and quoted me $4,000. I have bills to pay.',
    found:       'I kept seeing women in their 20s all the way to their 60s raving about this Korean hair growth serum.',
    noDrugs:     'No minoxidil, no finasteride.',
    routine:     "I've been using this every morning for the past four months.",
    clients:     'Of course I had to put all of my clients onto it that were willing to try it.',
    clientsWhat: 'It has been filling in gaps, strengthening their ends, decreasing shedding. Even people I had very little hope for.',
    babyhairs:   'My baby hairs are literally erupting from my scalp.',
    feel:        "It doesn't leave any grease or residue.",
    stock:       'People were pissed because they had to wait over a month to order theirs.'
  };

  /* ---------- VOICED: written for her, from what she said ---------- */
  /* Rules for adding to this register:
       - it must expand a SAID line, not invent an event
       - it may not add a result, a number, a timeline or a claim about the
         formula. Those are VEYLA's or they do not go on the page
       - it may not put a feeling in her mouth that contradicts the tape     */
  const VOICED = {
    openHook: {
      from: 'shower',
      text: 'It did not start with a bald patch. It started with a drain I stopped looking at.'
    },
    openBody: {
      from: 'job',
      text: 'I do hair for a living. I have my hands in somebody else’s scalp all day, I hear about thinning all day, and then I would go home and find mine on the shower wall. You cannot really hide from it in this job. You are looking at hairlines from eight inches away for eight hours.'
    },
    clinicBefore: {
      from: 'clinic',
      text: 'I did the sensible thing first. I went to a doctor, because that is what you are supposed to do, and I want to be fair to them: what they offered me is the stuff that actually has the evidence behind it.'
    },
    clinicAfter: {
      from: 'clinic',
      text: 'Then they said the number. Four thousand. I am 23. I have rent, I have a car, I have a chair to keep full. It was not that I thought it would not work. It was that it was not a number I had.'
    },
    forever: {
      from: 'clinic',
      text: 'The part that stopped me was not the price though. It was realising that the prescriptions are not a thing you finish. You take them, and if you stop, you go back. At 23 that is a very long time to sign up for something.'
    },
    foundBody: {
      from: 'found',
      text: 'So I did what everybody does and I went looking. I was not expecting anything. I have watched enough women in my chair spend money on shampoos that do nothing to be pretty cynical about it. What got me was the range of ages. It was not all 22-year-olds with thick hair already.'
    },
    myselfFirst: {
      from: 'clients',
      text: 'I want to be clear about the order this happened in, because it matters. I used it on myself first, every morning, before I said a word about it to anybody in my chair. I am not putting something on a client’s scalp to find out what it does.'
    },
    clientsBody: {
      from: 'clientsWhat',
      text: 'Then I offered it to the ones who were willing, and I got to do the thing nobody selling you a serum can do: I watched the same heads, month after month, under the same lights. Some of them I honestly did not expect anything from.'
    },
    feelBody: {
      from: 'feel',
      text: 'Texture is the whole reason I still use it, and I do not think people take that seriously enough. Everything heavy enough to feel like it is working is the thing you end up washing straight back out. A treatment you rinse off is a treatment you quit.'
    },
    controlBody: {
      from: 'routine',
      text: 'The last reason is the boring one and it is the one I would say to a client. This is the only version of dealing with my hair that I can stop whenever I want. No appointment to cancel, no prescription to come off, no package I already paid for. It is one minute in the morning and it is mine.'
    },
    close: {
      from: 'routine',
      text: 'I am four months in. I am not going to sit here and tell you what your hair will do, because I have no idea, and anybody who does tell you that is lying to you. I can tell you what mine did and that I kept buying it.'
    }
  };

  /* ---------- FALLBACK: what each reason says with OFF = true ---------- */
  const FALLBACK = {
    openHook: 'It rarely starts with a bald patch. It starts with a drain you stop looking at.',
    openBody: 'Thinning in your twenties is common, and it is usually well underway before it is obvious enough to photograph.',
    clinicBefore: 'The clinic route is the one with the evidence behind it, and it is fair to say so.',
    clinicAfter: 'It is also priced in four figures, which puts it out of reach for most women in their twenties.',
    forever: 'The prescriptions are not a course you finish. Published guidance is that gains reverse within months of stopping.',
    foundBody: 'The serum aisle is full of things that do nothing, which is a reasonable reason to be sceptical of this one too.',
    myselfFirst: 'Veyla is a daily topical, used on your own scalp, on your own schedule.',
    clientsBody: 'Reviews on the store report less shedding first, then density later.',
    feelBody: 'Texture decides whether a scalp treatment survives past week two.',
    controlBody: 'There is no appointment, no prescription and no course to pay up front.',
    close: 'Individual results vary, and no topical can promise a specific outcome.'
  };

  /* ---------- VEYLA: the brand's own claims, in the brand's voice ---------- */
  const VEYLA = {
    actives: 'Caffeine, fenugreek and menthol, applied with the built-in brush.',
    exosome: {
      figure: '1,600x',
      text: 'Veyla publishes that its nanoexosome carriers are around 1,600x smaller than a pore, which is the basis of its absorption claim.',
      note: 'That figure is Veyla’s own, from its FAQ. It has not been independently verified and it is printed here as the brand’s claim rather than as a finding.'
    },
    /* The transcript names BIOTIN. This tube does not contain biotin. See REFUSED. */
    timeline: 'Shedding is the first thing that can visibly change, because it does not require new hair to grow. Density is a week 12 read at the earliest, in the same light and the same parting. Week three is where most people stop, and nothing has failed by week three.',
    /* Deliberately NOT the footer's wording. The footer carries the full legal
       block and it is always on; this is the in-body version, and duplicating
       the footer here would put the same sentence on the page twice. */
    medical: 'Both of the drugs she was offered work, and the published guidance is that the gains reverse within months of stopping either one. Finasteride is prescription only and is prescribed off-label for women.',
    guaranteeNote: 'Veyla publishes a money-back guarantee on its own site. Its wording and its refund policy have not been reconciled, so this page quotes no window and no percentage. What it quotes instead is the subscription term, which is checkable: skip, delay or cancel before the next charge.'
  };

  /* ---------- OUTSIDE: what the alternatives cost ---------- */
  const OUTSIDE = {
    transplant:  { cost: '$4,000 to $15,000 one time', note: 'Typical published US range for a follicular unit procedure. Surgical, and priced per graft.' },
    minoxidil:   { cost: '$10 to $50 a month, ongoing', note: 'Over the counter. Published guidance is that gains reverse within months of stopping.' },
    finasteride: { cost: '$20 to $70 a month, ongoing', note: 'Prescription only, and prescribed off-label for women. A clinician question, not a shelf question.' },
    salon:       { cost: '$100 to $300 a session', note: 'In-clinic scalp treatments, priced per visit and usually sold as a course.' }
  };

  /* ---------- REFUSED: in the transcript, not on the page ---------- */
  const REFUSED = {
    biotin:
      'She names biotin as an ingredient. This tube does not contain biotin ' +
      '(caffeine, fenugreek, menthol). Her formula sentence is not quoted anywhere ' +
      'and the ingredient panel prints the real actives in Veyla’s voice.',
    numberOne:
      '"The number one Korean hair growth serum" is a ranking with no source. Not printed.',
    noChemicals:
      '"No chemicals, no long-term effects" is not a safety claim anyone can stand ' +
      'behind for a topical. The page says what is true instead: no prescription, ' +
      'and nothing you have to keep taking to keep the result.',
    transplantFraming:
      'Her hook is that she got a "Korean hair transplant". Not printed in any ' +
      'register: this is a cosmetic serum and it transplants nothing. Saying it in ' +
      'HER voice, in first person, would be worse than saying it in ours.',
    rapunzel:
      'Her Rapunzel line is a joke about her own hair. Nothing on this page ' +
      'promises length.',
    hundreds:
      '"Hundreds of people showing their transformation" is hers and is not printed. ' +
      'The stockout line IS printed, attributed, with the page saying in Veyla’s ' +
      'voice that it carries no timer, counter or deadline.'
  };

  /* said(key)  -> verbatim, or '' when off
     voiced(key)-> her voice, or the brand's fallback when off
     Both go through here so no caller has to know about the switch. */
  const said   = k => (OFF ? '' : (SAID[k] || ''));
  const voiced = k => (OFF ? (FALLBACK[k] || '') : ((VOICED[k] || {}).text || ''));

  window.VR_SOURCE = {
    OFF, BYLINE, SAID, VOICED, FALLBACK, VEYLA, OUTSIDE, REFUSED,
    said, voiced,
    /* kept so a caller cannot accidentally treat a voiced line as verbatim */
    isVerbatim: k => !OFF && !!SAID[k]
  };
})();
